/**
 * Lifeseeds free-gift quantity sync.
 *
 * Free first-order gifts are added by assets/custom-subscription-widget.js as
 * one-time cart lines tagged with _gift plus the parent they belong to
 * (_gift_for = variant id, _gift_plan = selling plan id or "0") and the ratio
 * they were earned at (_gift_ratio = "GIFTS:UNITS"). This file keeps those
 * lines in step with the parent afterwards: raise the subscription to 2 and the
 * gift follows to 2, drop back to 1 and the extra jar goes away, remove the
 * subscription and the gift goes with it.
 *
 * Two paths:
 *
 * 1. Quantity changes made in the cart page / cart drawer. CartItems.prototype
 *    .updateQuantity is wrapped: instead of Dawn's single-line /cart/change.js,
 *    the customer's line and the gift line go out together in one key-addressed
 *    /cart/update.js call. One request, one render, so the gift never visibly
 *    lags a step behind, and Shopify applies the set atomically, so a rejected
 *    subscription change can't leave a gift adjusted for it.
 *
 * 2. Everything else (adds from a product page, another tab, a stale cart on
 *    page load) is caught by a reconcile pass over /cart.js on cartUpdate,
 *    which does its own single re-render.
 *
 * Only lines carrying _gift_for are touched. Gift lines added before this
 * existed have no ratio to sync against and are left exactly as they were.
 */
(function () {
  if (window.lifeseedsGiftSync) return;
  window.lifeseedsGiftSync = true;

  const GIFT_MAX = 99;

  function parseRatio(raw) {
    const parts = String(raw || '').split(':');
    const gifts = parseInt(parts[0], 10);
    const units = parseInt(parts[1], 10);
    if (!Number.isFinite(gifts) || gifts < 1) return null;
    return { gifts, units: Number.isFinite(units) && units > 0 ? units : 1 };
  }

  // Gifts scale with the parent but never disappear while the parent is in the
  // cart: a 1-gift-per-2-units ratio at 3 units floors to 1, not 1.5.
  function targetQuantity(ratio, parentQuantity) {
    if (parentQuantity <= 0) return 0;
    const scaled = Math.floor((ratio.gifts * parentQuantity) / ratio.units);
    return Math.min(GIFT_MAX, Math.max(1, scaled));
  }

  function same(a, b) {
    return String(a || '0') === String(b || '0');
  }

  /**
   * @param {Array} lines normalized cart lines (see linesFromDom / linesFromCart)
   * @returns {Object} { lineKey: targetQuantity } for gift lines that are off
   */
  function computeGiftUpdates(lines) {
    const updates = {};

    lines.forEach((line) => {
      if (!line.giftFor) return;
      const ratio = parseRatio(line.giftRatio);
      if (!ratio) return;

      const parentQuantity = lines.reduce((sum, candidate) => {
        // A gift line is never somebody else's parent.
        if (candidate.giftFor) return sum;
        if (!same(candidate.variant, line.giftFor)) return sum;
        if (!same(candidate.plan, line.giftPlan)) return sum;
        return sum + candidate.quantity;
      }, 0);

      const target = targetQuantity(ratio, parentQuantity);
      if (target !== line.quantity) updates[line.key] = target;
    });

    return updates;
  }

  function linesFromDom(scope) {
    return Array.from(scope.querySelectorAll('[data-line-key]')).map((el) => ({
      key: el.dataset.lineKey,
      variant: el.dataset.lineVariant,
      plan: el.dataset.linePlan,
      quantity: parseInt(el.dataset.lineQty, 10) || 0,
      index: parseInt(el.dataset.lineIndex, 10) || 0,
      giftFor: el.dataset.giftFor || '',
      giftPlan: el.dataset.giftPlan || '0',
      giftRatio: el.dataset.giftRatio || '',
    }));
  }

  function linesFromCart(cart) {
    return (cart.items || []).map((item) => {
      const properties = item.properties || {};
      return {
        key: item.key,
        variant: item.variant_id,
        plan: (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan.id) || 0,
        quantity: item.quantity,
        index: 0,
        giftFor: properties._gift_for || '',
        giftPlan: properties._gift_plan || '0',
        giftRatio: properties._gift_ratio || '',
      };
    });
  }

  function postUpdates(updates, sections) {
    const config = fetchConfig();
    const body = { updates };
    if (sections) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }
    config.body = JSON.stringify(body);
    return fetch(`${routes.cart_update_url}`, config).then((response) => response.json());
  }

  /* ------------------------------------------------------------------ *
   * Path 1: the customer's own quantity change, sent together with the gift
   * correction so the cart moves in one atomic step and renders once.
   * ------------------------------------------------------------------ */

  function patchCartItems() {
    if (typeof CartItems === 'undefined' || CartItems.prototype.lifeseedsGiftSyncPatched) return false;

    const original = CartItems.prototype.updateQuantity;

    CartItems.prototype.updateQuantity = function (line, quantity, name, variantId) {
      const lines = linesFromDom(this);
      const changed = lines.find((candidate) => candidate.index === parseInt(line, 10));

      // Gift lines have no quantity controls of their own; anything else that
      // no gift hangs off goes straight down Dawn's path.
      if (!changed || changed.giftFor) return original.call(this, line, quantity, name, variantId);

      const projected = lines.map((candidate) =>
        candidate.key === changed.key ? { ...candidate, quantity: parseInt(quantity, 10) || 0 } : candidate,
      );
      const updates = computeGiftUpdates(projected);
      if (!Object.keys(updates).length) return original.call(this, line, quantity, name, variantId);

      updates[changed.key] = parseInt(quantity, 10) || 0;
      return this.lifeseedsUpdateLines(line, changed.key, updates, name, variantId);
    };

    /**
     * Dawn's updateQuantity for several lines at once: /cart/update.js instead
     * of /cart/change.js, addressed by line key rather than position. Shopify
     * applies the whole set or none of it, so the gift can't end up adjusted
     * for a subscription change that was itself rejected. The response
     * handling below mirrors CartItems.updateQuantity in assets/cart.js.
     */
    CartItems.prototype.lifeseedsUpdateLines = function (line, changedKey, updates, name, variantId) {
      this.enableLoading(line);

      const config = fetchConfig();
      config.body = JSON.stringify({
        updates,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });

      return fetch(`${routes.cart_update_url}`, config)
        .then((response) => response.text())
        .then((state) => {
          const parsedState = JSON.parse(state);
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);

          if (parsedState.errors || parsedState.status) {
            if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors || parsedState.description);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) ||
              document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector,
            );
          });

          // Removing a gift line shifts everything below it, so the line the
          // customer touched is found by key, not by the index it had before.
          const updatedIndex = (parsedState.items || []).findIndex((item) => item.key === changedKey) + 1;
          const updatedValue = updatedIndex ? parsedState.items[updatedIndex - 1].quantity : undefined;
          let message = '';
          if (quantityElement && updatedValue !== parseInt(quantityElement.value)) {
            message =
              typeof updatedValue === 'undefined'
                ? window.cartStrings.error
                : window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
          this.updateLiveRegions(line, message);

          const focusLine = updatedIndex || line;
          const lineItem =
            document.getElementById(`CartItem-${focusLine}`) || document.getElementById(`CartDrawer-Item-${focusLine}`);
          if (lineItem && name && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }

          // 'cart-items' keeps the cart's own elements from redrawing on top of
          // the sections just rendered, exactly as Dawn's own update does.
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId });
        })
        .catch(() => {
          this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
          const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
          if (errors) errors.textContent = window.cartStrings.error;
        })
        .finally(() => {
          this.disableLoading(line);
        });
    };

    CartItems.prototype.lifeseedsGiftSyncPatched = true;
    return true;
  }

  /* ------------------------------------------------------------------ *
   * Path 2: everything that changes the cart from somewhere else.
   * ------------------------------------------------------------------ */

  let reconciling = false;

  function renderIconBubble(cart) {
    const bubble = document.getElementById('cart-icon-bubble');
    const html = cart && cart.sections && cart.sections['cart-icon-bubble'];
    if (!bubble || !html) return;
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
    if (source) bubble.innerHTML = source.innerHTML;
  }

  function applyUpdates(updates) {
    if (!Object.keys(updates).length) return Promise.resolve(null);
    return postUpdates(updates, ['cart-icon-bubble']).then((cart) => {
      renderIconBubble(cart);
      // cart-items / cart-drawer-items redraw themselves off this event; the
      // source keeps our own subscriber from looping on it.
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-sync', cartData: cart });
      return cart;
    });
  }

  function reconcileFromCart() {
    if (reconciling) return;
    reconciling = true;
    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => applyUpdates(computeGiftUpdates(linesFromCart(cart))))
      .catch((error) => console.error(error))
      .finally(() => {
        reconciling = false;
      });
  }

  // On load the server-rendered rows already carry everything we need. Scoped
  // to one container: on the cart page the drawer is in the DOM too, and
  // counting both copies of every line would double each parent's quantity.
  function reconcileFromDom() {
    const scope = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');
    if (!scope || !scope.querySelector('[data-gift-for]')) return;
    const updates = computeGiftUpdates(linesFromDom(scope));
    if (!Object.keys(updates).length) return;
    reconciling = true;
    applyUpdates(updates)
      .catch((error) => console.error(error))
      .finally(() => {
        reconciling = false;
      });
  }

  function init() {
    patchCartItems();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.source === 'gift-sync') return;
        reconcileFromCart();
      });
    }
    reconcileFromDom();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
